import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
    Box, Chip, Dialog, Divider, TextField, Typography,
    Slide, Avatar, List, ListItem, ListItemAvatar, ListItemText, ListItemButton,
    Button, MenuItem, Select, IconButton, ToggleButtonGroup, ToggleButton, Stack
} from "@mui/material";
import { PAContext } from "../../../Services/PAContext";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import SupplyItem from "../../../MasterEventObjects/SupplyItem";
import { capitalizeFirstLetter } from "../../Helpers/TextStyling";
import { getAllContactsFromDatabase } from "../../Restricted/Contacts/BrowserDatabaseFunctions";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function SuppliesAddNewDialog({ open, onClose, selectedAttendeeId }) {
    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account, SampleAttendees, authenticated } = dataArray;

    // --- State ---
    const [dbContacts, setDbContacts] = useState([]);
    const [itemName, setItemName] = useState("");
    const [category, setCategory] = useState("supplies");
    const [subCategory, setSubCategory] = useState("General");
    const [customSubCategory, setCustomSubCategory] = useState("");
    const [isPrivate, setIsPrivate] = useState(false);
    const [isEssential, setIsEssential] = useState(false);
    const [selectedAssignees, setSelectedAssignees] = useState([]);
    const [itemNotes, setItemNotes] = useState("");

    // --- Data Fetching & Helpers (Logic remains same) ---
    useEffect(() => {
        if (!authenticated) { setDbContacts([]); return; }
        getAllContactsFromDatabase().then(contacts => setDbContacts(Array.isArray(contacts) ? contacts : []));
    }, [authenticated]);

    const [ attendeesReferenceArray, setAttendeesReferenceArray ] = useState( authenticated
        ? dbContacts
        : SampleAttendees);

    useEffect(() => {
        console.log("authenticated, dbContacts, SampleAttendees triggered ...");
        setAttendeesReferenceArray(
            authenticated
                ? dbContacts
                : SampleAttendees
        );
    }, [authenticated, dbContacts, SampleAttendees]);

    const sortedAttendees = useMemo(() => {
        return [...(PA_GlobalObject?.Planning?.attendees || [])].sort((a, b) =>
            (a.nickname || a.fname || "").localeCompare(b.nickname || b.fname || "")
        );
    }, [PA_GlobalObject?.Planning?.attendees]);

    const filteredContacts = useMemo(() => {
        const attendeeIds = new Set(PA_GlobalObject?.Planning?.attendees.map(a => a.id));
        return attendeesReferenceArray
            .filter(c => !attendeeIds.has(c.id))
            .sort((a, b) => (a.nickname || a.fname || "").localeCompare(b.nickname || b.fname || ""));
    }, [attendeesReferenceArray, sortedAttendees]);

    useEffect(() => {
        if (open) {
            setItemName(""); setCategory("supplies"); setSubCategory("General");
            setCustomSubCategory(""); setIsPrivate(false); setIsEssential(false);
            setSelectedAssignees(selectedAttendeeId ? [selectedAttendeeId] : [Account.id]);
            setItemNotes("");
        }
    }, [open, selectedAttendeeId]);

    const toggleAssignee = (id) => {
        setSelectedAssignees(prev => {
            if (id === "0") return ["0"];
            const filtered = prev.filter(a => a !== "0");
            if (filtered.includes(id)) {
                const next = filtered.filter(a => a !== id);
                return next.length === 0 ? ["0"] : next;
            } else { return [...filtered, id]; }
        });
    };

    const handleSave = () => {
        if (!itemName.trim()) return;
        const finalSubCategory = subCategory === "Custom" ? customSubCategory : subCategory;
        const newItems = selectedAssignees.map(assigneeId => {
            const newItem = new SupplyItem();
            newItem.name = itemName.trim();
            newItem.category = category;
            newItem.sub_category = finalSubCategory || "General";
            newItem.assigned_by = Account.id;
            newItem.assigned_to = assigneeId;
            newItem.private = isPrivate;
            newItem.essential = isEssential;
            newItem.notes = itemNotes;
            newItem.status = 1;
            return newItem;
        });

        set_PA_GlobalObject((prev) => ({
            ...prev, Planning: { ...prev.Planning, supplies: [...(prev.Planning.supplies || []), ...newItems] }
        }));

        if (PA_GlobalObject.Planning.status !== 10) {
            newItems.forEach(item => sendSocketMessage({ action: "E_addNewSupplyItem", event_id: PA_GlobalObject.Planning.id, supply_item: item }));
        }
        onClose();
    };

    const renderPersonRow = (person, isContact = false) => {
        const attendeeAvatar = attendeesReferenceArray.find((_att) => _att.id === person.id);
        const isSelected = selectedAssignees.includes(person.id);
        const displayName = person.nickname?.trim() ? person.nickname : `${person.fname || ''} ${person.lname || ''}`.trim();
        return (
            <ListItem disablePadding key={person.id}>
                <ListItemButton onClick={() => toggleAssignee(person.id)} sx={{ borderRadius: '12px', mb: 0.5, py: 1.5, backgroundColor: isSelected ? 'rgba(21, 18, 105, 0.04)' : 'transparent', border: isSelected ? '1.5px solid #151269' : '1px solid #eef0f2' }}>
                    <ListItemAvatar>
                        <Avatar src={attendeeAvatar?.avatar?.preview} sx={{ width: 40, height: 40, border: isSelected ? '2px solid #151269' : 'none' }}>
                            {displayName ? displayName[0].toUpperCase() : "?"}
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                        primary={capitalizeFirstLetter(displayName)}
                        secondary={isContact ? "Contact" : "Event Attendee"}
                        primaryTypographyProps={{ fontSize: '15px', fontWeight: isSelected ? 800 : 500, color: isSelected ? '#151269' : '#2c3e50' }}
                    />
                    {isSelected && <i className="icon-check" style={{ color: '#151269', fontSize: '1.2rem' }} />}
                </ListItemButton>
            </ListItem>
        );
    };

    // --- Logic: Dynamic Sub-Categories ---
    const dynamicSubCategories = useMemo(() => {
        // 1. Get all current supplies
        const currentSupplies = PA_GlobalObject?.Planning?.supplies || [];

        // 2. Filter by the currently selected top-level category (supplies or food)
        const existingSubs = currentSupplies
            .filter(item => item.category === category)
            .map(item => item.sub_category?.trim());

        // 3. Define your hardcoded defaults
        const defaults = ["General", "Cooking", "Shelter", "Tools", "Hygiene"];

        // 4. Combine, Normalize Case, and Remove Duplicates
        // Use a Set to ensure uniqueness
        const combined = new Set([...defaults]);

        existingSubs.forEach(sub => {
            if (!sub) return;
            // Check if a case-insensitive version already exists
            const exists = Array.from(combined).some(
                existing => existing.toLowerCase() === sub.toLowerCase()
            );
            if (!exists) {
                combined.add(sub);
            }
        });

        // 5. Return sorted list, ensuring "Custom" is always at the end
        return Array.from(combined).sort();
    }, [PA_GlobalObject?.Planning?.supplies, category]);


    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            PaperProps={{
                sx: { borderRadius: '20px', backgroundColor: '#fff', overflow: 'hidden' }
            }}
        >
            {/* COMMON HEADER */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f3f5' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#151269' }}>Manual Entry</Typography>
                <IconButton onClick={onClose} sx={{ color: '#e74c3c', backgroundColor: 'rgba(231, 76, 60, 0.1)', '&:hover': { backgroundColor: 'rgba(231, 76, 60, 0.2)' } }}>
                    <i className="icon-cross" />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
                {/* ITEM NAME */}
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, letterSpacing: 1 }}>ITEM NAME</Typography>
                <TextField
                    fullWidth
                    variant="standard"
                    placeholder="What are we bringing?"
                    value={itemName}
                    onChange={(e) => setItemName(e.target.value)}
                    autoFocus
                    InputProps={{ disableUnderline: true, sx: { fontSize: '1.8rem', fontWeight: 800, color: '#2c3e50' } }}
                    sx={{ mb: 4 }}
                />

                {/* VISUAL CATEGORY SELECTS */}
                <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, mb: 1, display: 'block' }}>CATEGORY</Typography>
                        <Select
                            fullWidth
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            sx={{ borderRadius: '12px', bgcolor: '#f8f9fa', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 700 }}
                        >
                            <MenuItem value="supplies">📦 Supplies</MenuItem>
                            <MenuItem value="food">🍕 Food</MenuItem>
                        </Select>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, mb: 1, display: 'block' }}>SUB-CATEGORY</Typography>
                        <Select
                            fullWidth
                            value={subCategory}
                            onChange={(e) => setSubCategory(e.target.value)}
                            sx={{ borderRadius: '12px', bgcolor: '#f8f9fa', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 700 }}
                        >
                            {/* Render dynamic list */}
                            {dynamicSubCategories.map(cat => (
                                <MenuItem key={cat} value={cat}>
                                    {/* Use capitalizeFirstLetter for a clean look */}
                                    {capitalizeFirstLetter(cat.toLowerCase())}
                                </MenuItem>
                            ))}

                            <Divider sx={{ my: 1 }} />

                            {/* Always keep Custom option at the bottom */}
                            <MenuItem value="Custom" sx={{ fontWeight: 800, color: '#151269' }}>
                                + Create New...
                            </MenuItem>
                        </Select>
                    </Box>
                </Stack>

                {subCategory === "Custom" && (
                    <TextField fullWidth placeholder="Enter custom name..." value={customSubCategory} onChange={(e) => setCustomSubCategory(e.target.value)} sx={{ mt: -3, mb: 4, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                )}

                {/* MODERN TOGGLES */}
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, mb: 1, display: 'block' }}>VISIBILITY</Typography>
                <ToggleButtonGroup
                    fullWidth
                    exclusive
                    value={isPrivate}
                    onChange={(e, val) => val !== null && setIsPrivate(val)}
                    sx={{ mb: 4, bgcolor: '#f1f3f5', p: 0.5, borderRadius: '14px', '& .MuiToggleButton-root': { border: 'none', borderRadius: '10px', py: 1, fontWeight: 700, textTransform: 'none', color: '#7f8c8d', '&.Mui-selected': { bgcolor: '#fff', color: '#151269', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' } } }}
                >
                    <ToggleButton value={false}>Public</ToggleButton>
                    <ToggleButton value={true}>Private</ToggleButton>
                </ToggleButtonGroup>

                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, mb: 1, display: 'block' }}>PRIORITY</Typography>
                <ToggleButtonGroup
                    fullWidth
                    exclusive
                    value={isEssential}
                    onChange={(e, val) => val !== null && setIsEssential(val)}
                    sx={{ mb: 4, bgcolor: '#f1f3f5', p: 0.5, borderRadius: '14px', '& .MuiToggleButton-root': { border: 'none', borderRadius: '10px', py: 1, fontWeight: 700, textTransform: 'none', color: '#7f8c8d', '&.Mui-selected': { bgcolor: '#fff', color: '#27ae60', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' } } }}
                >
                    <ToggleButton value={false}>One-time Item</ToggleButton>
                    <ToggleButton value={true}>✨ Essential (Save to Vault)</ToggleButton>
                </ToggleButtonGroup>

                {/* NOTES (As requested - same look) */}
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, display: 'block' }}>NOTES & INSTRUCTIONS</Typography>
                <TextField fullWidth multiline rows={2} placeholder="Add details..." value={itemNotes} onChange={(e) => setItemNotes(e.target.value)} sx={{ mt: 1, mb: 4, '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '12px' } }} />

                <Divider sx={{ mb: 3 }} />

                {/* ASSIGNMENT (As requested - same look but refined border) */}
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, mb: 1, display: 'block' }}>ASSIGN TO</Typography>
                <List>
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => toggleAssignee("0")} sx={{ borderRadius: '12px', mb: 1, py: 1.5, border: selectedAssignees.includes("0") ? '1.5px solid #e74c3c' : '1px solid #fdf2f2', bgcolor: selectedAssignees.includes("0") ? 'rgba(231, 76, 60, 0.04)' : 'transparent' }}>
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: '#fdf2f2', color: '#e74c3c', border: '1px dashed #e74c3c' }}>
                                    <i className="icon-notification" />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary="Bounty (Unassigned)" primaryTypographyProps={{ fontWeight: 800, color: '#e74c3c' }} />
                        </ListItemButton>
                    </ListItem>
                    {sortedAttendees.map(a => renderPersonRow(a))}
                    {filteredContacts.length > 0 && (
                        <>
                            <Typography variant="overline" sx={{ color: '#bdc3c7', fontWeight: 800, mt: 2, display: 'block', px: 2 }}>From Contacts</Typography>
                            {filteredContacts.map(c => renderPersonRow(c, true))}
                        </>
                    )}
                </List>
            </Box>

            {/* COMMON FOOTER */}
            <Box sx={{ p: 2, borderTop: '1px solid #f1f3f5', display: 'flex', gap: 2, bgcolor: '#fff' }}>
                <Button fullWidth onClick={onClose} sx={{ py: 1.5, borderRadius: '12px', fontWeight: 800, color: '#95a5a6', bgcolor: '#f8f9fa' }}>
                    Cancel
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSave}
                    disabled={!itemName.trim()}
                    sx={{ py: 1.5, borderRadius: '12px', fontWeight: 800, bgcolor: '#151269', '&:hover': { bgcolor: '#1a1780' }, boxShadow: '0 4px 14px rgba(21, 18, 105, 0.25)' }}
                >
                    {selectedAssignees.length > 1 ? `Add ${selectedAssignees.length} Items` : "Add Item"}
                </Button>
            </Box>
        </Dialog>
    );
}

export default SuppliesAddNewDialog;